// How a part puts its block into the Workspace sidebar (and its screens under
// /<slug>/...). The Workspace screens import no part: the host lists the
// sections (`workspaceScreen({ sections })`), the same way Me lists its parts.
//
// Promise:
// - Sections are shown in `order` (Inbox 10, DM 20, Topics 30, Calls 35,
//   Documents 40, Issues 50). Ids are unique.
// - A section tied to a feature is left out when that feature is off: a
//   workspace feature by the workspace, a space feature when no space that is
//   not archived has it on.
// - `realtime` handlers get the team events (the workspace window's stream);
//   the same type may have handlers in several sections.
import type { QueryClient } from "@tanstack/react-query";
import type { AnyRoute } from "@tanstack/react-router";
import type {
  SpaceView,
  TeamEventView,
  WorkspaceView,
} from "@trameria/cloud-api/workspace-shared";
import type { Api } from "@trameria/ui-core";
import type { ComponentType, ReactNode } from "react";

export interface TeamRealtimeContext {
  qc: QueryClient;
  /** For a handler that reads exactly what changed and puts it in place. */
  api: Api;
  event: TeamEventView;
  /** The slug of a workspace the person is in (from the cached list), if known. */
  slugOf(workspaceId: string): string | null;
}

export type TeamRealtimeHandler = (
  payload: Record<string, unknown>,
  context: TeamRealtimeContext,
) => void;

export type TeamRealtimeHandlers = Readonly<
  Record<string, TeamRealtimeHandler>
>;

/**
 * An item a part adds to the workspace's settings (`/<slug>/settings/<id>`),
 * placed among the Workspace part's own items of its group by `order`.
 */
export interface WorkspaceSettingsEntry {
  id: string;
  group: "personal" | "admin";
  /**
   * Smaller comes first. The Workspace part's own admin items: general 10,
   * features 20, spaces 30, members 40, roles 50, invites 70. Without one, the
   * item comes after them.
   */
  order?: number;
  label(): string;
  /** The item's mark in the settings sidebar (all there is of it when folded). */
  icon: ComponentType<{ className?: string }>;
  description(): string;
  /** Whether the person may use it (else it is neither listed nor shown). */
  allowed(can: (permission: string) => boolean): boolean;
  Component: ComponentType;
}

export interface WorkspaceSection {
  id: string;
  /** Smaller comes first. */
  order: number;
  label(): string;
  icon: ComponentType<{ className?: string }>;
  /** A path inside the workspace ("/topics" opens /<slug>/topics). */
  to: string;
  /**
   * One of my own things (the Inbox, mine and the workspace's together): its
   * row sits above the line, with the rows to Me's pages.
   */
  mine?: boolean;
  /**
   * More paths inside the workspace that are this section's (its row is the
   * current one there too): Issues' projects, cycles and upcoming.
   */
  paths?: readonly string[];
  /** The feature the section is about; the section goes when it is off. */
  feature?: string;
  /** A dot or a count, only when going there finds something to do. */
  useBadge?(): ReactNode;
  /** Routes under /<slug> (`workspace` is the workspace's frame). */
  routes?(workspace: AnyRoute): AnyRoute[];
  realtime?: TeamRealtimeHandlers;
  /** Items this part adds to the workspace's settings. */
  settings?: readonly WorkspaceSettingsEntry[];
  /**
   * Where opening the workspace itself (`/<slug>`) sends the person, a path
   * inside the workspace; null for nowhere. The first shown section in order
   * that names one wins.
   */
  landing?(spaces: readonly SpaceView[]): string | null;
}

export function defineWorkspaceSection(
  section: WorkspaceSection,
): WorkspaceSection {
  if (!/^[a-z][a-z0-9-]*$/.test(section.id))
    throw new Error(`workspace section id must be kebab-case: ${section.id}`);
  if (!section.to.startsWith("/"))
    throw new Error(`workspace section path must start with /: ${section.to}`);
  return section;
}

/** The sections in order; refuses two with the same id. */
export function orderSections(
  sections: readonly WorkspaceSection[],
): WorkspaceSection[] {
  const ids = new Set<string>();
  for (const s of sections) {
    if (ids.has(s.id)) throw new Error(`duplicate workspace section: ${s.id}`);
    ids.add(s.id);
  }
  return [...sections].sort((a, b) => a.order - b.order);
}

/** Whether a section's feature is on somewhere the person can go. */
export function sectionShown(
  section: WorkspaceSection,
  view: WorkspaceView,
  spaces: readonly SpaceView[],
): boolean {
  const feature = section.feature;
  if (!feature) return true;
  const flag = view.available.features.find((f) => f.name === feature);
  if (flag?.scope === "workspace") return view.features.includes(feature);
  return spaces.some(
    (s) => s.archivedAt === null && s.features.includes(feature),
  );
}
